import {
  IonAlert,
  IonButton,
  IonIcon,
  IonItem,
  IonList,
  IonPopover,
  IonSpinner,
} from '@ionic/react';
import { linkOutline, logOutOutline, trashOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { once, run } from '../../../helper/electron';
import { useIcons } from '../../../hooks/useIcons';
import AppleMusicService from '../../../services/AppleMusic.service';
import { CloudCrateService } from '../../../services/CloudCrate.service';
import SpotifyService from '../../../services/Spotify.service';
import { findStreamingMatch } from '../../../services/StreamingMatch.service';
import TidalService from '../../../services/Tidal.service';
import { useDispatch, useSelector } from '../../../store/hooks';
import {
  AppIdentifiers,
  setToken,
} from '../../../store/slices/token-state.slice';
import { iCrate } from '../../../types/ICrate';
import { iTrack } from '../../../types/ITrack';
import { copyToClipboard } from '../../../utils/copyToClipboard';
import isElectron from '../../../utils/isElectron';
import { openExternalLink } from '../../../utils/openExternalLink';
import { useToast } from '../../ToastManager/useToast';
import {
  CloudSaveStatusPanel,
  UnmatchedCloudTrack,
} from './CloudSaveStatusPanel';
import './StreamingSaveButtons.scss';

interface StreamingSaveButtonsProps {
  crateData: iCrate;
  tracks: iTrack[];
  compact?: boolean;
}

const showToastMessage = (
  toastFn: (toast: { message: string }) => void,
  message: string,
) => {
  toastFn({ message });
};

type ServiceId = 'spotify' | 'apple' | 'tidal';

interface ServiceState {
  connected: boolean;
  saving: boolean;
  saved: boolean;
  progress: number;
  error?: string;
  needsSync?: boolean; // True if playlist exists but track count differs
  savedTrackCount?: number; // Track count when last saved
  playlistId?: string; // Existing playlist ID for sync updates
  missingTrackIndices?: number[]; // Indices of tracks not in the streaming playlist
  existingTrackIds?: string[]; // Track IDs already in the streaming playlist
}

export const StreamingSaveButtons: React.FC<StreamingSaveButtonsProps> = ({
  crateData,
  tracks,
  compact = false,
}) => {
  const dispatch = useDispatch();
  const spotifyToken = useSelector(
    (state) => state.tokenState.spotify?.accessToken,
  );
  const { icons } = useIcons();
  const { successToast, errorToast } = useToast();

  // Initial service states (will be updated from local DB)
  const [serviceStates, setServiceStates] = useState<
    Record<ServiceId, ServiceState>
  >({
    spotify: { connected: false, saving: false, saved: false, progress: 0 },
    apple: { connected: false, saving: false, saved: false, progress: 0 },
    tidal: { connected: false, saving: false, saved: false, progress: 0 },
  });

  const [appleMusicConnected, setAppleMusicConnected] = useState(
    AppleMusicService.isAuthorized(),
  );
  const [tidalConnected, setTidalConnected] = useState(
    TidalService.isConnected(),
  );

  const isSpotifyConnected = !!spotifyToken;

  // Track if component is mounted to prevent state updates after unmount
  // This allows exports to continue in the background without React warnings
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Safe state update wrappers - only update if component is still mounted
  const safeSetServiceStates = useCallback(
    (
      updater:
        | Record<ServiceId, ServiceState>
        | ((
            prev: Record<ServiceId, ServiceState>,
          ) => Record<ServiceId, ServiceState>),
    ) => {
      if (isMountedRef.current) {
        setServiceStates(updater);
      }
    },
    [],
  );

  const safeSetStatusPanel = useCallback(
    (
      updater:
        | typeof statusPanel
        | ((prev: typeof statusPanel) => typeof statusPanel),
    ) => {
      if (isMountedRef.current) {
        setStatusPanel(updater as any);
      }
    },
    [],
  );

  // Status panel state
  const [statusPanel, setStatusPanel] = useState<{
    visible: boolean;
    service: ServiceId | null;
    status: string;
    currentTrack: string;
    matchedCount: number;
    totalCount: number;
    unmatched: UnmatchedCloudTrack[];
    phase: 'matching' | 'creating' | 'adding' | 'done' | 'error';
  }>({
    visible: false,
    service: null,
    status: '',
    currentTrack: '',
    matchedCount: 0,
    totalCount: 0,
    unmatched: [],
    phase: 'matching',
  });

  // Error dialog state
  const [errorAlert, setErrorAlert] = useState<{
    isOpen: boolean;
    service: string;
    message: string;
  }>({
    isOpen: false,
    service: '',
    message: '',
  });

  // Disconnect confirmation state
  const [disconnectAlert, setDisconnectAlert] = useState<{
    isOpen: boolean;
    service: ServiceId | null;
  }>({
    isOpen: false,
    service: null,
  });

  // Options popover state
  const [optionsPopover, setOptionsPopover] = useState<{
    isOpen: boolean;
    service: ServiceId | null;
    event: React.MouseEvent | undefined;
  }>({
    isOpen: false,
    service: null,
    event: undefined,
  });

  // Load saved states from local database when crateData changes
  // Also identify which specific tracks are missing from each streaming playlist
  useEffect(() => {
    const loadSavedServicesAndCheckSync = async () => {
      if (!crateData?._id || !tracks?.length) return;

      // Get full save details including playlist IDs
      const saveDetails = await CloudCrateService.getSavesForCrate(
        crateData._id,
      );

      console.log('[CloudCrates] Loaded save details:', saveDetails);

      const newStates: Partial<Record<ServiceId, Partial<ServiceState>>> = {};

      for (const save of saveDetails) {
        const serviceId = save.serviceId as ServiceId;
        if (!['spotify', 'apple', 'tidal'].includes(serviceId)) continue;

        // Compare against the CRATE track count when we last saved, not playlist count
        // This way, if 32/39 tracks matched on first save, we don't show "needs sync"
        // We only show sync if the user has ADDED tracks to the crate since last save
        const savedCrateTrackCount = save.trackCount; // Crate size when we saved
        const currentCrateTrackCount = tracks.length;

        console.log(
          `[CloudCrates] ${serviceId}: saved when crate had ${savedCrateTrackCount} tracks, now has ${currentCrateTrackCount}`,
        );

        // Needs sync if crate now has MORE tracks than when we last saved
        const needsSync = currentCrateTrackCount > savedCrateTrackCount;
        const missingCount = Math.max(
          0,
          currentCrateTrackCount - savedCrateTrackCount,
        );

        // Generate missing indices (the last N tracks that were added)
        const missingTrackIndices: number[] = [];
        if (needsSync) {
          // New tracks are at the end of the crate
          for (let i = savedCrateTrackCount; i < currentCrateTrackCount; i++) {
            missingTrackIndices.push(i);
          }
        }

        console.log(
          `[CloudCrates] ${serviceId}: needsSync=${needsSync}, ${missingCount} new tracks since last save`,
        );

        newStates[serviceId] = {
          saved: true,
          needsSync,
          savedTrackCount: savedCrateTrackCount,
          playlistId: save.playlistId,
          missingTrackIndices,
          existingTrackIds: [],
        };
      }

      safeSetServiceStates((prev) => ({
        ...prev,
        spotify: {
          ...prev.spotify,
          saved: !!newStates.spotify?.saved,
          needsSync: newStates.spotify?.needsSync || false,
          savedTrackCount: newStates.spotify?.savedTrackCount,
          playlistId: newStates.spotify?.playlistId,
          missingTrackIndices: newStates.spotify?.missingTrackIndices,
          existingTrackIds: newStates.spotify?.existingTrackIds,
        },
        apple: {
          ...prev.apple,
          saved: !!newStates.apple?.saved,
          needsSync: newStates.apple?.needsSync || false,
          savedTrackCount: newStates.apple?.savedTrackCount,
          playlistId: newStates.apple?.playlistId,
          missingTrackIndices: newStates.apple?.missingTrackIndices,
          existingTrackIds: newStates.apple?.existingTrackIds,
        },
        tidal: {
          ...prev.tidal,
          saved: !!newStates.tidal?.saved,
          needsSync: newStates.tidal?.needsSync || false,
          savedTrackCount: newStates.tidal?.savedTrackCount,
          playlistId: newStates.tidal?.playlistId,
          missingTrackIndices: newStates.tidal?.missingTrackIndices,
          existingTrackIds: newStates.tidal?.existingTrackIds,
        },
      }));
    };

    loadSavedServicesAndCheckSync();
  }, [crateData?._id, tracks, safeSetServiceStates]);

  // Check connection status on mount
  useEffect(() => {
    const checkConnections = async () => {
      await AppleMusicService.initialize();

      safeSetServiceStates((prev) => ({
        ...prev,
        spotify: {
          ...prev.spotify,
          connected: isSpotifyConnected,
        },
        apple: {
          ...prev.apple,
          connected: !!appleMusicConnected,
        },
        tidal: {
          ...prev.tidal,
          connected: !!tidalConnected,
        },
      }));
    };
    checkConnections();
  }, [
    isSpotifyConnected,
    appleMusicConnected,
    tidalConnected,
    safeSetServiceStates,
  ]);

  const updateServiceState = useCallback(
    (serviceId: ServiceId, updates: Partial<ServiceState>) => {
      safeSetServiceStates((prev) => ({
        ...prev,
        [serviceId]: { ...prev[serviceId], ...updates },
      }));
    },
    [safeSetServiceStates],
  );

  // Handle authentication for a service
  const handleAuth = async (serviceId: ServiceId) => {
    if (serviceId === 'spotify') {
      if (isElectron()) {
        try {
          const authUrl = await SpotifyService.getSpotifyAuthorizeUrl(
            window.location.hash,
          );
          run('loginSpotify', {
            authUrl,
            redirectUri: 'cratehackersx://spotify/callback',
          });
          once(
            'loginSpotify',
            async (result: { code?: string; error?: string }) => {
              if (result.error) {
                showToastMessage(errorToast, 'Spotify authentication failed');
                return;
              }
              if (result.code) {
                try {
                  const tokenResponse = await SpotifyService.getToken(
                    result.code,
                  );
                  if (tokenResponse.access_token) {
                    const expiry = Date.now() + tokenResponse.expires_in * 1000;
                    dispatch(
                      setToken(
                        AppIdentifiers.Spotify,
                        tokenResponse.access_token,
                        tokenResponse.refresh_token,
                        expiry,
                      ),
                    );
                    updateServiceState('spotify', { connected: true });
                    showToastMessage(successToast, 'Connected to Spotify!');
                  } else {
                    showToastMessage(
                      errorToast,
                      tokenResponse.error_description ||
                        'Failed to get Spotify token',
                    );
                  }
                } catch (tokenError) {
                  console.error('[Spotify] Token exchange error:', tokenError);
                  showToastMessage(
                    errorToast,
                    'Failed to complete Spotify login',
                  );
                }
              }
            },
          );
        } catch (error) {
          console.error('[Spotify] Auth error:', error);
        }
      } else {
        const authUrl = await SpotifyService.getSpotifyAuthorizeUrl(
          window.location.hash,
        );
        window.location.href = authUrl;
      }
    } else if (serviceId === 'tidal') {
      if (isElectron()) {
        try {
          const authData = await TidalService.getTidalAuthData(
            window.location.hash,
          );
          run('loginTidal', {
            authUrl: authData.authUrl,
            redirectUri: authData.redirectUri,
          });
          once(
            'loginTidal',
            async (result: {
              tokens?: any;
              code?: string;
              codeVerifier?: string;
              error?: string;
            }) => {
              if (result.error) {
                showToastMessage(errorToast, 'Tidal authentication failed');
                return;
              }

              let tokens = result.tokens;

              // If we got a code, exchange it for tokens
              if (result.code && !tokens) {
                console.log('[Tidal] Got code, exchanging for tokens...');
                console.log(
                  '[Tidal] CodeVerifier provided:',
                  !!result.codeVerifier,
                );
                try {
                  const tokenResponse = await TidalService.getToken(
                    result.code,
                    result.codeVerifier,
                  );
                  console.log('[Tidal] Token response:', tokenResponse);
                  // API may return camelCase (accessToken) or snake_case (access_token)
                  const accessToken =
                    tokenResponse.accessToken || tokenResponse.access_token;
                  const refreshToken =
                    tokenResponse.refreshToken || tokenResponse.refresh_token;
                  const expiresIn =
                    tokenResponse.expiresIn || tokenResponse.expires_in;

                  if (accessToken) {
                    tokens = {
                      accessToken,
                      refreshToken,
                      expiresIn,
                    };
                  } else {
                    console.error(
                      '[Tidal] Token exchange failed:',
                      tokenResponse,
                    );
                    showToastMessage(
                      errorToast,
                      tokenResponse.error_description ||
                        tokenResponse.error ||
                        'Failed to get Tidal token',
                    );
                    return;
                  }
                } catch (tokenError) {
                  console.error('[Tidal] Token exchange error:', tokenError);
                  showToastMessage(
                    errorToast,
                    'Failed to complete Tidal login',
                  );
                  return;
                }
              }

              if (tokens) {
                const expiry = Date.now() + tokens.expiresIn * 1000;
                dispatch(
                  setToken(
                    AppIdentifiers.Tidal,
                    tokens.accessToken,
                    tokens.refreshToken,
                    expiry,
                  ),
                );
                localStorage.setItem('tidal_access_token', tokens.accessToken);
                localStorage.setItem(
                  'tidal_refresh_token',
                  tokens.refreshToken,
                );
                localStorage.setItem('tidal_token_expiry', String(expiry));
                setTidalConnected(true);
                updateServiceState('tidal', { connected: true });
                showToastMessage(successToast, 'Connected to Tidal!');
              }
            },
          );
        } catch (error) {
          console.error('[Tidal] Auth error:', error);
        }
      } else {
        const authUrl = await TidalService.getTidalAuthorizeUrl(
          window.location.hash,
        );
        window.location.href = authUrl;
      }
    } else if (serviceId === 'apple') {
      if (isElectron()) {
        try {
          const developerToken = await AppleMusicService.fetchDeveloperToken();
          run('loginAppleMusic', { developerToken });
          once(
            'loginAppleMusic',
            async (result: { userToken?: string; error?: string }) => {
              if (result.error) {
                showToastMessage(
                  errorToast,
                  'Apple Music authentication failed',
                );
                return;
              }
              if (result.userToken) {
                await AppleMusicService.setUserToken(result.userToken);
                setAppleMusicConnected(true);
                updateServiceState('apple', { connected: true });
                showToastMessage(successToast, 'Connected to Apple Music!');
              }
            },
          );
        } catch (error) {
          console.error('[Apple Music] Auth error:', error);
        }
      } else {
        try {
          const authorized = await AppleMusicService.authorize();
          if (authorized) {
            setAppleMusicConnected(true);
            updateServiceState('apple', { connected: true });
            showToastMessage(successToast, 'Connected to Apple Music!');
          }
        } catch (error) {
          console.error('Apple Music authorization failed:', error);
        }
      }
    }
  };

  // Save crate to a streaming service
  const saveToService = async (serviceId: ServiceId) => {
    console.log(`[CloudCrates] Starting save to ${serviceId}...`);
    console.log(`[CloudCrates] Tracks to save: ${tracks?.length || 0}`);
    console.log(`[CloudCrates] Service state:`, {
      playlistId: serviceStates[serviceId].playlistId,
      needsSync: serviceStates[serviceId].needsSync,
      saved: serviceStates[serviceId].saved,
      missingTrackIndices: serviceStates[serviceId].missingTrackIndices,
    });

    if (!tracks || tracks.length === 0) {
      showToastMessage(errorToast, 'No tracks to save');
      return;
    }

    const totalTracks = tracks.length;
    const existingPlaylistId = serviceStates[serviceId].playlistId;
    const isSyncing =
      !!existingPlaylistId && serviceStates[serviceId].needsSync;

    console.log(
      `[CloudCrates] existingPlaylistId: ${existingPlaylistId}, isSyncing: ${isSyncing}`,
    );

    // Initialize status panel
    safeSetStatusPanel({
      visible: true,
      service: serviceId,
      status: isSyncing ? 'Checking playlist...' : 'Matching tracks...',
      currentTrack: '',
      matchedCount: 0,
      totalCount: totalTracks,
      unmatched: [],
      phase: 'matching',
    });

    updateServiceState(serviceId, {
      saving: true,
      progress: 0,
      error: undefined,
    });

    try {
      const matchedTrackIds: string[] = [];
      const unmatchedTracks: UnmatchedCloudTrack[] = [];
      const existingTrackIds = serviceStates[serviceId].existingTrackIds || [];
      const missingIndices = serviceStates[serviceId].missingTrackIndices || [];

      // For Spotify with large crates: Create playlist FIRST to avoid rate limit issues
      // This prevents the pattern of "100+ search calls then create" which triggers 400 errors
      let earlyPlaylistId: string | null = null;
      const playlistName = crateData.Title || 'Crate Hackers Playlist';
      const desc = crateData.Description ? ': ' + crateData.Description : '';
      const playlistDescription = `Saved from Crate Hackers${desc}`;

      if (serviceId === 'spotify' && !isSyncing && tracks.length > 50) {
        console.log(
          '[CloudCrates] Large crate - creating Spotify playlist BEFORE matching',
        );
        console.log('[CloudCrates] Original name:', playlistName);
        console.log('[CloudCrates] Original description:', playlistDescription);
        safeSetStatusPanel((prev) => ({
          ...prev,
          status: 'Creating playlist...',
          phase: 'creating',
        }));

        const playlist = await SpotifyService.createPlaylist(
          playlistName,
          playlistDescription,
        );
        earlyPlaylistId = playlist?.id;
        console.log('[CloudCrates] Playlist created early:', earlyPlaylistId);

        safeSetStatusPanel((prev) => ({
          ...prev,
          status: 'Matching tracks...',
          phase: 'matching',
        }));
      }

      // Determine which tracks to match
      let tracksToMatch: iTrack[];
      if (isSyncing && missingIndices.length > 0) {
        // Smart sync: only match the missing tracks (already identified on load)
        tracksToMatch = missingIndices.map((idx) => tracks[idx]);
        console.log(
          `[CloudCrates] Smart sync: only matching ${tracksToMatch.length} missing tracks`,
        );
        safeSetStatusPanel((prev) => ({
          ...prev,
          status: `Matching ${tracksToMatch.length} new tracks...`,
          totalCount: tracksToMatch.length,
        }));
      } else {
        // Full save: match all tracks
        tracksToMatch = tracks;
      }

      // Match each track
      for (let i = 0; i < tracksToMatch.length; i++) {
        const track = tracksToMatch[i];
        if (!track) continue;

        const trackTitle = track.Title || 'Unknown Title';
        const trackArtist = track.Artist || 'Unknown Artist';
        const trackDisplay =
          trackTitle.length > 30
            ? trackTitle.substring(0, 30) + '...'
            : trackTitle;

        console.log(
          `[CloudCrates] Matching track ${i + 1}/${tracksToMatch.length}: ${trackArtist} - ${trackTitle}`,
        );

        safeSetStatusPanel((prev) => ({
          ...prev,
          status: `Matching track ${i + 1}/${tracksToMatch.length}`,
          currentTrack: trackDisplay,
        }));

        updateServiceState(serviceId, {
          progress: Math.round(((i + 1) / tracksToMatch.length) * 50),
        });

        const matchResult = await findStreamingMatch(track, serviceId);
        if (matchResult.matched?.ID) {
          console.log(`[CloudCrates] ✓ Found match: ${matchResult.matched.ID}`);
          matchedTrackIds.push(matchResult.matched.ID);
          safeSetStatusPanel((prev) => ({
            ...prev,
            matchedCount: matchedTrackIds.length,
          }));
        } else {
          console.log(`[CloudCrates] ✗ No match found`);
          unmatchedTracks.push({
            artist: trackArtist,
            title: trackTitle,
            reason: matchResult.reason || 'Not found',
          });
        }

        // Delay to avoid rate limiting - use longer delay for large track lists
        if (i < tracksToMatch.length - 1) {
          // 200ms for large lists, 100ms for smaller ones
          const delay = tracksToMatch.length > 50 ? 200 : 100;
          await new Promise((r) => setTimeout(r, delay));
        }
      }

      // Longer pause before playlist creation to let Spotify rate limits reset
      // Spotify may throttle after many search requests
      if (tracksToMatch.length > 100) {
        console.log(
          '[CloudCrates] Large crate - pausing 5s before playlist creation...',
        );
        safeSetStatusPanel((prev) => ({
          ...prev,
          status: 'Preparing playlist...',
        }));
        await new Promise((r) => setTimeout(r, 5000));
      } else if (tracksToMatch.length > 50) {
        console.log('[CloudCrates] Pausing 2s before playlist creation...');
        await new Promise((r) => setTimeout(r, 2000));
      }

      console.log(
        `[CloudCrates] Matched ${matchedTrackIds.length}/${tracksToMatch.length} tracks`,
      );

      // For smart sync, matchedTrackIds already contains only the new tracks
      const newTrackIds = matchedTrackIds;

      if (matchedTrackIds.length === 0) {
        if (isSyncing) {
          // No new tracks to add
          safeSetStatusPanel((prev) => ({
            ...prev,
            phase: 'done',
            status: 'Playlist is already up to date!',
            currentTrack: '',
          }));

          updateServiceState(serviceId, {
            progress: 100,
            saving: false,
            saved: true,
            needsSync: false,
            savedTrackCount: totalTracks,
            missingTrackIndices: [],
          });

          showToastMessage(successToast, 'Playlist is already up to date!');

          setTimeout(() => {
            safeSetStatusPanel((prev) =>
              prev.phase === 'done' ? { ...prev, visible: false } : prev,
            );
          }, 3000);
          return;
        }
        throw new Error('No tracks could be matched');
      }

      if (isSyncing) {
        safeSetStatusPanel((prev) => ({
          ...prev,
          phase: 'adding',
          status: 'Updating playlist...',
          currentTrack: '',
        }));
        console.log(
          `[CloudCrates] Syncing existing playlist: ${existingPlaylistId}`,
        );
      } else {
        safeSetStatusPanel((prev) => ({
          ...prev,
          phase: 'creating',
          status: 'Creating playlist...',
          currentTrack: '',
        }));
      }

      // Debug: Export full crate data for troubleshooting
      console.log('[CloudCrates] Full crate data for debugging:', {
        crateId: crateData._id,
        title: crateData.Title,
        description: crateData.Description,
        trackCount: tracks.length,
        matchedCount: matchedTrackIds.length,
        matchedTrackIds: matchedTrackIds,
      });
      // Make crate data available in console for inspection
      (window as any).__debugCrateData = {
        crate: crateData,
        tracks: tracks,
        matchedTrackIds: matchedTrackIds,
      };

      updateServiceState(serviceId, { progress: 60 });

      // Use early-created playlist if available, otherwise use existing or create new
      let playlistId: string | null =
        earlyPlaylistId || existingPlaylistId || null;

      // Use newTrackIds for sync (only missing tracks), matchedTrackIds for new playlist
      const tracksToAdd = isSyncing ? newTrackIds : matchedTrackIds;

      // Tidal reports a rejected batch instead of throwing.
      let allTracksAdded = true;

      if (serviceId === 'spotify') {
        if (isSyncing && playlistId && newTrackIds.length > 0) {
          // Only add the missing tracks (smart sync)
          console.log(
            `[CloudCrates] Adding ${newTrackIds.length} missing tracks to Spotify`,
          );
          const uris = newTrackIds.map((id) => `spotify:track:${id}`);
          await SpotifyService.addTracksToPlaylist(playlistId, uris);
        } else if (
          isSyncing &&
          playlistId &&
          tracks.length < existingTrackIds.length
        ) {
          // Crate has fewer tracks - need to replace (tracks were removed)
          console.log(`[CloudCrates] Replacing tracks in Spotify playlist`);
          const uris = matchedTrackIds.map((id) => `spotify:track:${id}`);
          await SpotifyService.replacePlaylistTracks(playlistId, uris);
        } else if (!isSyncing) {
          // Check if playlist was already created early (for large crates)
          if (!playlistId) {
            // Create new playlist (for smaller crates or non-Spotify early creation)
            console.log(`[CloudCrates] Creating playlist: ${playlistName}`);
            const playlist = await SpotifyService.createPlaylist(
              playlistName,
              playlistDescription,
            );
            playlistId = playlist?.id;
          }

          if (playlistId && matchedTrackIds.length > 0) {
            safeSetStatusPanel((prev) => ({
              ...prev,
              phase: 'adding',
              status: 'Adding tracks to playlist...',
            }));
            updateServiceState(serviceId, { progress: 80 });
            const uris = matchedTrackIds.map((id) => `spotify:track:${id}`);
            await SpotifyService.addTracksToPlaylist(playlistId, uris);
          }
        }
      } else if (serviceId === 'apple') {
        if (isSyncing && playlistId && newTrackIds.length > 0) {
          // Only add the missing tracks (smart sync)
          console.log(
            `[CloudCrates] Adding ${newTrackIds.length} missing tracks to Apple Music`,
          );
          safeSetStatusPanel((prev) => ({
            ...prev,
            phase: 'adding',
            status: `Adding ${newTrackIds.length} new tracks...`,
          }));
          updateServiceState(serviceId, { progress: 80 });
          await AppleMusicService.addTracksToPlaylist(playlistId, newTrackIds);
        } else if (!isSyncing) {
          console.log(`[CloudCrates] Creating playlist: ${playlistName}`);
          playlistId = await AppleMusicService.createPlaylist(
            playlistName,
            playlistDescription,
          );
          if (playlistId && matchedTrackIds.length > 0) {
            safeSetStatusPanel((prev) => ({
              ...prev,
              phase: 'adding',
              status: 'Adding tracks to playlist...',
            }));
            updateServiceState(serviceId, { progress: 80 });
            await AppleMusicService.addTracksToPlaylist(
              playlistId,
              matchedTrackIds,
            );
          }
        }
      } else if (serviceId === 'tidal') {
        if (isSyncing && playlistId && newTrackIds.length > 0) {
          // Only add the missing tracks (smart sync)
          console.log(
            `[CloudCrates] Adding ${newTrackIds.length} missing tracks to Tidal`,
          );
          safeSetStatusPanel((prev) => ({
            ...prev,
            phase: 'adding',
            status: `Adding ${newTrackIds.length} new tracks...`,
          }));
          updateServiceState(serviceId, { progress: 80 });
          allTracksAdded = await TidalService.addTracksToPlaylist(
            playlistId,
            newTrackIds,
          );
        } else if (
          isSyncing &&
          playlistId &&
          tracks.length < existingTrackIds.length
        ) {
          // Crate has fewer tracks - need to clear and replace
          console.log(`[CloudCrates] Replacing tracks in Tidal playlist`);
          await TidalService.clearPlaylist(playlistId);
          safeSetStatusPanel((prev) => ({
            ...prev,
            phase: 'adding',
            status: 'Replacing playlist tracks...',
          }));
          updateServiceState(serviceId, { progress: 80 });
          allTracksAdded = await TidalService.addTracksToPlaylist(
            playlistId,
            matchedTrackIds,
          );
        } else if (!isSyncing) {
          console.log(`[CloudCrates] Creating playlist: ${playlistName}`);
          const tidalResponse = await TidalService.createPlaylist(
            playlistName,
            playlistDescription,
          );
          playlistId = tidalResponse?.data?.id;
          if (playlistId && matchedTrackIds.length > 0) {
            safeSetStatusPanel((prev) => ({
              ...prev,
              phase: 'adding',
              status: 'Adding tracks to playlist...',
            }));
            updateServiceState(serviceId, { progress: 80 });
            allTracksAdded = await TidalService.addTracksToPlaylist(
              playlistId,
              matchedTrackIds,
            );
          }
        }
      }

      const serviceName =
        serviceId === 'spotify'
          ? 'Spotify'
          : serviceId === 'apple'
            ? 'Apple Music'
            : 'Tidal';

      const unmatchedNote =
        unmatchedTracks.length > 0
          ? ` ${unmatchedTracks.length} not found.`
          : '';
      const savedMessage = allTracksAdded
        ? `Saved ${matchedTrackIds.length}/${totalTracks} tracks to ${serviceName}.${unmatchedNote}`
        : `Matched ${matchedTrackIds.length}/${totalTracks} tracks, but some could not be added to ${serviceName}.${unmatchedNote}`;

      // Update to done state
      safeSetStatusPanel((prev) => ({
        ...prev,
        phase: 'done',
        status: savedMessage,
        currentTrack: '',
        unmatched: unmatchedTracks,
      }));

      updateServiceState(serviceId, {
        progress: 100,
        saving: false,
        saved: true,
        needsSync: false,
        savedTrackCount: totalTracks,
        playlistId: playlistId || undefined,
        missingTrackIndices: [],
        existingTrackIds: [],
      });

      // Persist saved state to local database
      if (crateData._id) {
        CloudCrateService.recordSave(crateData._id, serviceId, {
          playlistId: playlistId || undefined,
          trackCount: totalTracks,
          matchedCount: matchedTrackIds.length,
        });
      }

      showToastMessage(
        allTracksAdded ? successToast : errorToast,
        savedMessage,
      );

      if (unmatchedTracks.length === 0 && allTracksAdded) {
        setTimeout(() => {
          safeSetStatusPanel((prev) =>
            prev.phase === 'done' ? { ...prev, visible: false } : prev,
          );
        }, 5000);
      }
    } catch (error: any) {
      console.error(`[StreamingSave] Error saving to ${serviceId}:`, error);

      // Hide status panel and show error dialog instead
      safeSetStatusPanel((prev) => ({
        ...prev,
        visible: false,
        phase: 'error',
        status: error.message || 'Failed to save',
        currentTrack: '',
      }));

      // Show error in a proper dialog
      const serviceName =
        serviceId === 'spotify'
          ? 'Spotify'
          : serviceId === 'apple'
            ? 'Apple Music'
            : 'Tidal';

      const rawMsg = error?.message || 'Failed to save';
      let formattedMsg = rawMsg;
      const lowerMsg = rawMsg.toLowerCase();
      if (
        lowerMsg.includes('reconnect') ||
        lowerMsg.includes('permission') ||
        lowerMsg.includes('token') ||
        lowerMsg.includes('unauthorized') ||
        lowerMsg.includes('401') ||
        lowerMsg.includes('403')
      ) {
        formattedMsg = `${serviceName} connection lost — please reconnect`;
      } else if (rawMsg.includes('Unrecognized Field')) {
        formattedMsg = `${serviceName} import error due to field mismatch. Please try reconnecting.`;
      }

      setErrorAlert({
        isOpen: true,
        service: serviceName,
        message: formattedMsg,
      });

      updateServiceState(serviceId, {
        saving: false,
        error: formattedMsg,
      });
    }
  };

  // Handle button click
  const handleServiceClick = (serviceId: ServiceId) => {
    const state = serviceStates[serviceId];
    const isConnected =
      serviceId === 'spotify' ? isSpotifyConnected : state.connected;

    if (state.saving) return; // Already in progress

    if (!isConnected) {
      handleAuth(serviceId);
      return;
    }

    if (!state.saved || state.needsSync) {
      // Save if not saved, or sync if needs update
      saveToService(serviceId);
    }
  };

  // Handle right-click to show options menu
  const handleServiceContextMenu = (
    e: React.MouseEvent,
    serviceId: ServiceId,
  ) => {
    e.preventDefault();
    const state = serviceStates[serviceId];
    if (state.connected || state.saved) {
      setOptionsPopover({ isOpen: true, service: serviceId, event: e });
    }
  };

  // Clear saved playlist link for a service
  const handleUnlinkPlaylist = async (serviceId: ServiceId) => {
    if (crateData?._id) {
      await CloudCrateService.removeSave(crateData._id, serviceId);
    }
    updateServiceState(serviceId, {
      saved: false,
      needsSync: false,
      playlistId: undefined,
      savedTrackCount: undefined,
      missingTrackIndices: [],
      existingTrackIds: [],
    });
    const serviceName =
      serviceId === 'spotify'
        ? 'Spotify'
        : serviceId === 'apple'
          ? 'Apple Music'
          : 'Tidal';
    showToastMessage(successToast, `Unlinked ${serviceName} playlist`);
  };

  // Delete playlist from streaming service and unlink
  const handleDeletePlaylist = async (serviceId: ServiceId) => {
    const state = serviceStates[serviceId];
    if (!state.playlistId) {
      showToastMessage(errorToast, 'No playlist to delete');
      return;
    }

    let deleted = false;
    const serviceName =
      serviceId === 'spotify'
        ? 'Spotify'
        : serviceId === 'tidal'
          ? 'Tidal'
          : 'Apple Music';

    if (serviceId === 'spotify') {
      deleted = await SpotifyService.deletePlaylist(state.playlistId);
    } else if (serviceId === 'tidal') {
      deleted = await TidalService.deletePlaylist(state.playlistId);
    }

    if (deleted) {
      // Also unlink locally
      if (crateData?._id) {
        await CloudCrateService.removeSave(crateData._id, serviceId);
      }
      updateServiceState(serviceId, {
        saved: false,
        needsSync: false,
        playlistId: undefined,
        savedTrackCount: undefined,
        missingTrackIndices: [],
        existingTrackIds: [],
      });
      showToastMessage(successToast, `${serviceName} playlist deleted`);
    } else {
      showToastMessage(errorToast, 'Failed to delete playlist');
    }
  };

  // Perform disconnect
  const handleDisconnect = (serviceId: ServiceId) => {
    if (serviceId === 'spotify') {
      SpotifyService.logout();
    } else if (serviceId === 'apple') {
      // Apple Music doesn't have a clear logout, but we can reset state
      localStorage.removeItem('apple_music_user_token');
      setAppleMusicConnected(false);
    } else if (serviceId === 'tidal') {
      TidalService.logout();
      setTidalConnected(false);
    }
    updateServiceState(serviceId, {
      connected: false,
      saved: false,
      needsSync: false,
    });
    const serviceName =
      serviceId === 'spotify'
        ? 'Spotify'
        : serviceId === 'apple'
          ? 'Apple Music'
          : 'Tidal';
    showToastMessage(successToast, `Disconnected from ${serviceName}`);
  };

  const services = [
    {
      id: 'spotify' as ServiceId,
      name: 'Spotify',
      icon: 'spotifyFull',
      color: '#1DB954',
    },
    {
      id: 'apple' as ServiceId,
      name: 'Apple Music',
      icon: 'apple',
      color: '#FC3C44',
    },
    {
      id: 'tidal' as ServiceId,
      name: 'Tidal',
      icon: 'tidal',
      color: '#00FFFF',
    },
  ];

  // Curator-provided external links (SoundCloud, Beatport, Deezer)
  const externalLinks = [
    {
      name: 'SoundCloud',
      icon: 'soundcloud',
      color: '#FF5500',
      url: crateData.StreamSoundcloud,
    },
    {
      name: 'Beatport',
      icon: 'beatport',
      color: '#02E680',
      url: crateData.StreamBeatsource
        ? crateData.StreamBeatsource.replace(/beatsource\.com/i, 'beatport.com')
        : '',
    },
    {
      name: 'Deezer',
      icon: 'deezer',
      color: '#FF0092',
      url: crateData.StreamDeezer,
    },
  ].filter((link) => link.url && link.url.trim() !== '');

  return (
    <div className={`streaming-save-buttons ${compact ? 'compact' : ''}`}>
      <div className="cloud-crates-header">
        {!compact && <span className="group-label">STREAMING</span>}
        <div className="service-buttons">
          {services.map((service) => {
            const state = serviceStates[service.id];
            const isConnected =
              service.id === 'spotify' ? isSpotifyConnected : state.connected;
            const connectedClass = isConnected ? 'connected' : '';
            const savedClass = state.saved ? 'saved' : '';
            const savingClass = state.saving ? 'saving' : '';
            const needsSyncClass = state.needsSync ? 'needs-sync' : '';
            const buttonClass = `service-btn ${service.id} ${connectedClass} ${savedClass} ${savingClass} ${needsSyncClass}`;

            // Build title text
            let title = isConnected
              ? `Save to ${service.name}`
              : `Connect ${service.name}`;
            if (state.saved && state.needsSync) {
              const trackDiff =
                (tracks?.length || 0) - (state.savedTrackCount || 0);
              title = `Update ${service.name} playlist (${trackDiff > 0 ? '+' : ''}${trackDiff} tracks)`;
            } else if (state.saved) {
              title = `Saved to ${service.name}`;
            }

            const showOptionsButton = isConnected || state.saved;

            return (
              <div
                key={service.id}
                className="service-btn-wrapper"
                data-tooltip={title}
              >
                <IonButton
                  className={buttonClass}
                  fill="clear"
                  onClick={() => handleServiceClick(service.id)}
                  onContextMenu={(e) => handleServiceContextMenu(e, service.id)}
                  disabled={state.saving}
                >
                  {state.saving ? (
                    <div className="saving-indicator">
                      <IonSpinner name="crescent" />
                      <span className="progress-text">{state.progress}%</span>
                    </div>
                  ) : (
                    <>
                      <IonIcon
                        icon={icons[service.icon]}
                        className={isConnected ? '' : 'not-connected'}
                        style={{
                          color: isConnected ? service.color : undefined,
                        }}
                      />
                      {state.saved && !state.needsSync && (
                        <span className="saved-check">✓</span>
                      )}
                      {state.saved && state.needsSync && (
                        <span className="sync-indicator">↻</span>
                      )}
                      {!isConnected && (
                        <span className="connect-hint">
                          <svg
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            width="10"
                            height="10"
                          >
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                          </svg>
                        </span>
                      )}
                    </>
                  )}
                </IonButton>
                {showOptionsButton && (
                  <button
                    className="ellipsis-btn"
                    onClick={(e) =>
                      setOptionsPopover({
                        isOpen: true,
                        service: service.id,
                        event: e,
                      })
                    }
                    title={`${service.name} options`}
                  >
                    ⋮
                  </button>
                )}
              </div>
            );
          })}

          {/* Curator-provided external links */}
          {externalLinks.map((link) => (
            <div
              key={link.name}
              className="export-btn-wrapper"
              data-tooltip={`Open on ${link.name}`}
            >
              <IonButton
                key={link.name}
                className="service-btn external-link"
                fill="clear"
                onClick={() => openExternalLink(link.url!)}
              >
                <IonIcon
                  icon={icons[link.icon]}
                  style={{ color: link.color }}
                />
              </IonButton>
            </div>
          ))}
        </div>
      </div>

      {/* Status Panel */}
      <CloudSaveStatusPanel
        visible={statusPanel.visible}
        serviceLabel={
          statusPanel.service === 'spotify'
            ? 'Spotify'
            : statusPanel.service === 'apple'
              ? 'Apple Music'
              : 'Tidal'
        }
        status={statusPanel.status}
        currentTrack={statusPanel.currentTrack}
        matchedCount={statusPanel.matchedCount}
        totalCount={statusPanel.totalCount}
        phase={statusPanel.phase}
        unmatched={statusPanel.unmatched}
        onClose={() =>
          safeSetStatusPanel((prev) => ({ ...prev, visible: false }))
        }
        onCopyUnmatched={
          statusPanel.unmatched.length
            ? () => {
                const text = statusPanel.unmatched
                  .map(
                    (track) =>
                      `${track.artist} - ${track.title} (${track.reason})`,
                  )
                  .join('\n');
                copyToClipboard(text).then((ok) => {
                  showToastMessage(
                    ok ? successToast : errorToast,
                    ok ? 'Unmatched tracks copied' : 'Could not copy list',
                  );
                });
              }
            : undefined
        }
      />

      {/* Error Dialog */}
      <IonAlert
        isOpen={errorAlert.isOpen}
        onDidDismiss={() =>
          setErrorAlert({ isOpen: false, service: '', message: '' })
        }
        header={`${errorAlert.service} Error`}
        message={errorAlert.message}
        buttons={['OK']}
      />

      {/* Disconnect Confirmation Dialog */}
      <IonAlert
        isOpen={disconnectAlert.isOpen}
        onDidDismiss={() =>
          setDisconnectAlert({ isOpen: false, service: null })
        }
        header="Disconnect Service"
        message={`Are you sure you want to disconnect from ${disconnectAlert.service === 'spotify' ? 'Spotify' : disconnectAlert.service === 'apple' ? 'Apple Music' : 'Tidal'}? You'll need to reconnect to save playlists.`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Disconnect',
            role: 'destructive',
            handler: () => {
              if (disconnectAlert.service) {
                handleDisconnect(disconnectAlert.service);
              }
            },
          },
        ]}
      />

      {/* Options Popover */}
      <IonPopover
        isOpen={optionsPopover.isOpen}
        event={optionsPopover.event}
        onDidDismiss={() =>
          setOptionsPopover({ isOpen: false, service: null, event: undefined })
        }
        dismissOnSelect={true}
        className="service-options-popover"
      >
        <IonList lines="none">
          {optionsPopover.service &&
            serviceStates[optionsPopover.service]?.saved && (
              <IonItem
                button
                detail={false}
                onClick={() => {
                  if (optionsPopover.service) {
                    handleUnlinkPlaylist(optionsPopover.service);
                  }
                }}
              >
                <IonIcon
                  slot="start"
                  icon={linkOutline}
                  style={{ marginRight: 8 }}
                />
                Unlink Playlist
              </IonItem>
            )}
          {optionsPopover.service &&
            (optionsPopover.service === 'spotify' ||
              optionsPopover.service === 'tidal') &&
            serviceStates[optionsPopover.service]?.saved &&
            serviceStates[optionsPopover.service]?.playlistId && (
              <IonItem
                button
                detail={false}
                onClick={() => {
                  if (optionsPopover.service) {
                    handleDeletePlaylist(optionsPopover.service);
                  }
                }}
                className="destructive-item"
              >
                <IonIcon
                  slot="start"
                  icon={trashOutline}
                  style={{ marginRight: 8, color: '#ff4444' }}
                />
                <span style={{ color: '#ff4444' }}>
                  Delete from{' '}
                  {optionsPopover.service === 'spotify' ? 'Spotify' : 'Tidal'}
                </span>
              </IonItem>
            )}
          {optionsPopover.service &&
            (optionsPopover.service === 'spotify'
              ? isSpotifyConnected
              : serviceStates[optionsPopover.service]?.connected) && (
              <IonItem
                button
                detail={false}
                onClick={() => {
                  if (optionsPopover.service) {
                    handleDisconnect(optionsPopover.service);
                  }
                }}
              >
                <IonIcon
                  slot="start"
                  icon={logOutOutline}
                  style={{ marginRight: 8 }}
                />
                Disconnect Account
              </IonItem>
            )}
        </IonList>
      </IonPopover>
    </div>
  );
};
